/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export interface FooterLinkItem {
    label: string;
    to: string;
}

export interface VersionEntry {
    license: string;
    name: string;
    version: string;
}

export interface ImprintDisclosureSection {
    content: string[];
    title: string;
}

const IMPORTANT_DEPENDENCIES = [
    'react',
    'react-dom',
    'react-router',
    'plotly.js-dist-min',
    'tailwindcss',
    'daisyui',
    'lucide-react',
    'typescript',
    'vite',
] as const;

export const CONTACT_DETAILS = {
    company: 'EA Systems Dresden GmbH',
    addressLines: ['Würzburger Str. 14', '01187 Dresden', 'Deutschland'],
    email: 'info@ea-energie.de',
    phone: '+49 351 467136 55',
    city: 'Dresden',
    country: 'Deutschland',
    copyright: '© ' + (new Date().getFullYear()),
} as const;

export const IMPRINT_SECTIONS = {
    contact: {
        content: ['Telefon: + 49 351 467 136 55', 'E-Mail: info@ea-energie.de'],
        title: 'Kontaktdaten',
    },
    legalNotes: [
        {
            content: [
                'Texte, Bilder, Grafiken, Ton-, Video- und Animationsdateien sowie ihre Arrangements auf der EA Systems Dresden Webseite unterliegen dem Schutz des Urheberrechts und anderen Schutzgesetzen. Der Inhalt darf weder für kommerzielle Zwecke oder zur Wiedergabe kopiert, noch verändert und auf anderen Webseiten verwendet werden. EA Systems Dresden weist darauf hin, dass auf der Website enthaltene Bilder teilweise Urheberrechten Dritter unterliegen.',
            ],
            title: 'Rechtliche Hinweise',
        },
        {
            content: [
                'Wenn nicht anders angegeben, sind alle Markenzeichen auf der EA Systems Dresden Webseite markenrechtlich geschützt. Dies gilt insbesondere für Marken, Typenbezeichnungen, Logos und Embleme.',
            ],
            title: 'Markenzeichen',
        },
        {
            content: [
                'Das auf der EA Systems Dresden Webseite enthaltene geistige Eigentum, wie Patente, Marken und Urheberrechte, ist geschützt. Durch die EA Systems Dresden Webseite wird keine Lizenz zur Nutzung von geistigem Eigentum der EA Systems Dresden GmbH oder Dritten erteilt.',
            ],
            title: 'Nutzungsrechte',
        },
        {
            content: [
                'Die EA Systems Dresden Webseite wurde mit größtmöglicher Sorgfalt erarbeitet. Die EA Systems Dresden GmbH bemüht sich, das Informationsangebot stetig zu erweitern und zu aktualisieren. Die EA Systems Dresden GmbH garantiert jedoch nicht für die Fehlerfreiheit und Genauigkeit der enthaltenen Informationen. EA Systems Dresden schließt jegliche Haftung aus, soweit sie nicht auf Vorsatz oder grober Fahrlässigkeit von EA Systems Dresden beruht.',
                'Wenn auf Internetseiten Dritter verwiesen wird (Links), übernimmt EA Systems Dresden keine Verantwortung für die Inhalte der verlinkten Seiten. Mit dem betätigen des Links verlassen Sie das Informationsangebot von EA Systems Dresden. Für die Angebote Dritter können abweichende Regelungen gelten, insbesondere hinsichtlich des Datenschutzes.',
            ],
            title: 'Haftungsausschluss',
        },
    ] as ImprintDisclosureSection[],
    managingDirector: {
        content: ['René Unger'],
        title: 'Geschäftsführer',
    },
    provider: {
        content: ['EA Systems Dresden GmbH', 'Würzburger Str. 14', '01187 Dresden'],
        title: 'Herausgeber & Diensteanbieter',
    },
    taxInfo: {
        content: [
            'Sitz der Gesellschaft: Dresden',
            'Amtsgericht Dresden HRB 31344',
            'Umsatzsteuer-Identnummer: DE 284551370',
        ],
        title: 'Steuerrechtliche Infos',
    },
} as const;

export const IMPORTANT_VERSION_ENTRIES: VersionEntry[] = IMPORTANT_DEPENDENCIES.map(
    (dependencyName) => ({
        license: __APP_DEPENDENCY_LICENSES__[dependencyName] ?? 'N/A',
        name: dependencyName,
        version: __APP_DEPENDENCY_VERSIONS__[dependencyName] ?? 'n/a',
    }),
).filter((entry) => entry.version !== 'n/a');

export const APPLICATION_CHANGELOG = __APP_CHANGELOG__;

export const APPLICATION_VERSION_MODIFIED_AT = __APP_VERSION_MODIFIED_AT__;

export const APPLICATION_VERSION = __APP_VERSION__;
